<template>
  <div class="flex flex-col gap-1">
    <div class="flex flex-row items-center">
      <div class="flex flex-1 flex-col">
        <h2 class="text-base font-bold">
          {{
            invoiceable.is_company && invoiceable.company_name
              ? invoiceable.company_name
              : invoiceable.full_name
          }}
          {{ invoiceable.is_deceased ? `(${$t('attributes.dead')})` : null }}
        </h2>
        <div
          v-if="invoiceable.is_company && invoiceable.full_name !== invoiceable.company_name && invoiceable.type === 'App\\Models\\Contact'"
          class="text-xs text-gray-500"
        >
          {{ invoiceable.full_name }}
        </div>
        <div
          v-if="invoiceable.type === 'App\\Models\\Person'"
          class="flex flex-row gap-2"
        >
          <div
            v-if="invoiceable.relationship"
            class="bg-primary text-contrast-primary capitalize-first rounded-md px-2 py-0.5 text-xs"
          >
            {{ relationshipLabel }}
          </div>
          <div
            v-if="invoiceable.is_purchaser"
            class="bg-secondary text-contrast-secondary capitalize-first rounded-md px-2 py-0.5 text-xs"
          >
            {{ $t('enums.contact.contactDossierTypes.purchaser') }}
          </div>
          <div
            v-if="invoiceable.is_handler"
            class="bg-secondary text-contrast-secondary capitalize-first rounded-md px-2 py-0.5 text-xs"
          >
            {{ $t('enums.contact.contactDossierTypes.handler') }}
          </div>
          <div
            v-if="invoiceable.is_co_handler"
            class="bg-secondary text-contrast-secondary capitalize-first rounded-md px-2 py-0.5 text-xs"
          >
            {{ $t('enums.contact.contactDossierTypes.co_handler') }}
          </div>
        </div>
      </div>
      <span
        v-if="
          !invoiceableDossierSelector
        "
        class="shrink-0"
      >
        <BaseDropDownMenu
          v-if="$can('manage_invoicing')"
          :options="menuOptions"
          :is-button="false"
          size="small"
        />
      </span>
      <span v-else-if="invoice.status === Status.DRAFT" class="shrink-0">
        <InvoiceableDossierSelector
          v-if="invoice.dossier?.id"
          v-model:invoiceable="localInvoiceable"
          v-model:invoiceable-type="localInvoiceableType"
          v-model:invoiceable-id="localInvoiceableId"
          :invoice-id="invoice.id"
          :selected-id="invoiceable.id"
          @invoiceable-changed="invoiceableChanged"
        />
      </span>
    </div>
    <p>
      <span>{{ invoiceable.street }}<br /></span>
      <span v-if="invoiceable.extra_street && invoiceable.extra_street.length">
        {{ invoiceable.extra_street }}<br />
      </span>
      <span>{{ [invoiceable.zip, invoiceable.city].join(' ') }}</span>
    </p>
    <div v-if="invoiceable.phone_field && invoiceable.phone_field.length">
      <div
        class="capitalize-first truncate text-xs leading-tight font-medium text-gray-500"
      >
        {{ $t('attributes.phone') }}
      </div>
      <div class="leading-tight font-medium text-gray-900">
        {{ invoiceable.phone_field }}
      </div>
    </div>
    <div v-if="invoiceable.mobile_field && invoiceable.mobile_field.length">
      <div
        class="capitalize-first truncate text-xs leading-tight font-medium text-gray-500"
      >
        {{ $t('attributes.mobile') }}
      </div>
      <div class="leading-tight font-medium text-gray-900">
        {{ invoiceable.mobile_field }}
      </div>
    </div>
    <div v-if="documentDeliveryPreferences.length">
      <div
        class="capitalize-first truncate text-xs leading-tight font-medium text-gray-500"
      >
        {{ $t('attributes.document_delivery_preference') }}
      </div>
      <div
        v-for="documentDeliveryPreference in documentDeliveryPreferences"
        :key="documentDeliveryPreference.id"
        class="flex flex-row items-center gap-1 leading-tight font-medium text-gray-900"
      >
        <FontAwesomeIcon :icon="['fal', documentDeliveryPreference.icon]" />
        <span>{{ documentDeliveryPreference.name }}</span>
      </div>
    </div>
    <ShowPersonModal
      v-model:open="isModalPersonOpen"
      :person-id="invoiceable.id"
      @person-updated="personUpdated"
    />
    <SelectContactModal
      v-if="isModalContactOpen"
      :open="isModalContactOpen"
      :contact-id="invoiceable.id"
      force-form
      @update:open="isModalContactOpen = $event"
      @contact-updated="contactUpdated"
    />
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import InvoiceableDossierSelector from '@/views/invoicing/show/tabs/information/components/information/components/InvoiceableDossierSelector.vue'
import { mapActions, mapGetters } from 'vuex'
import BaseDropDownMenu from '@c/addf-package/components/dropdown/BaseDropDownMenu.vue'
import ShowPersonModal from '@c/Person/component/ShowPersonModal.vue'
import SelectContactModal from '@ui/modal/contact/SelectContactModal.vue'
import Status from '@/assets/enums/invoicing/Status'
import StatusInput from '@c/addf-package/components/status/StatusInput.vue'
import ConfirmModal from '@c/addf-package/components/modal/ConfirmModal.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'

export default defineComponent({
  name: 'InvoiceableSelector',
  components: {
    FontAwesomeIcon,
    ShowPersonModal,
    SelectContactModal,
    BaseDropDownMenu,
    InvoiceableDossierSelector
  },
  props: {
    invoice: {
      type: Object,
      required: true
    },
    invoiceable: {
      type: Object,
      required: true
    },
    invoiceableId: {
      type: String,
      required: true
    },
    invoiceableType: {
      type: String,
      required: true
    },
    invoiceableDossierSelector: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  emits: [
    'invoiceable-changed',
    'update:invoiceableType',
    'update:invoiceableId',
    'update:invoiceable',
    'update:invoice'
  ],
  data() {
    return {
      isModalPersonOpen: false,
      isModalContactOpen: false,
      selectedChoice: null
    }
  },
  computed: {
    ...mapGetters({
      getRelationshipMetadata: 'metadata/getRelationshipMetadata',
      document_delivery_preferences:
        'metadata/getPersonCommunicationMethodePreferences'
    }),
    localInvoice: {
      get() {
        return this.invoice
      },
      set(value) {
        this.$emit('update:invoice', value)
      }
    },
    Status() {
      return Status
    },
    relationshipLabel() {
      if (this.invoiceable.relationship) {
        return this.getRelationshipMetadata(this.invoiceable.relationship)
      }
      return null
    },
    documentDeliveryPreferences() {
      if (!Array.isArray(this.invoiceable?.document_delivery_preference)) {
        return []
      }
      return (
        this.document_delivery_preferences.filter(
          (document_delivery_preference) =>
            this.invoiceable.document_delivery_preference.includes(
              document_delivery_preference.id
            )
        ) ?? []
      )
    },
    menuOptions() {
      const isContact = this.invoiceableType === 'App\\Models\\Contact'
      const isPerson = this.invoiceableType === 'App\\Models\\Person'

      return [
        [
          {
            label: this.$t('global.card'),
            icon: 'address-card',
            gate: isPerson,
            action: () =>
              this.$router.push({
                name: 'person_view_route',
                params: { personId: this.invoiceableId }
              })
          },
          {
            label: this.$t('invoicing.edit_contact'),
            icon: 'pen',
            gate: isPerson,
            disabled: !this.$can('manage_invoicing_contact'),
            tooltip: this.$can('manage_invoicing_contact')
              ? null
              : this.$t('global.missing_permission_for_action', 'Une permission est manquante pour effectuer cette action'),
            action: () => (this.isModalPersonOpen = true)
          },
          {
            label: this.$t('invoicing.edit_contact'),
            icon: 'pen',
            gate: isContact,
            disabled: !this.$can('manage_invoicing_contact'),
            tooltip: this.$can('manage_invoicing_contact')
              ? null
              : this.$t('global.missing_permission_for_action', 'Une permission est manquante pour effectuer cette action'),
            action: () => (this.isModalContactOpen = true)
          },
          {
            label: this.$t('invoicing.replace_contact'),
            icon: 'address-card',
            gate: true,
            disabled: this.invoice.status !== Status.DRAFT,
            action: () => {
              this.$router.push({
                name: 'invoice_edit_route',
                query: { replace: true }
              })
            }
          }
        ]
      ]
    },
    localInvoiceable: {
      get() {
        return this.invoiceable
      },
      set(value) {
        return this.$emit('update:invoiceable', value)
      }
    },
    localInvoiceableId: {
      get() {
        return this.invoiceableId
      },
      set(value) {
        return this.$emit('update:invoiceableId', value)
      }
    },
    localInvoiceableType: {
      get() {
        return this.invoiceableType
      },
      set(value) {
        return this.$emit('update:invoiceableType', value)
      }
    }
  },
  methods: {
    invoiceableChanged() {
      this.$emit('invoiceable-changed')
    },
    personUpdated() {
      this.$emit('invoiceable-changed')
    },
    contactUpdated() {
      this.$emit('invoiceable-changed')
    }
  }
})
</script>
